import React from 'react';
import { useTheme } from '../contexts/ThemeContext';
// import { useDesign } from '../contexts/DesignContext';
import { designConfig } from '../design/designConfig';

interface MetricCardDesignedProps {
  title: string;
  subtitle?: string;
  titleValue: number;
  titlePrefix?: string;
  titleSuffix?: string;
  subtitleValue?: number;
  smallBoxValue?: number;
  smallBoxPrefix?: string;
  smallBoxSuffix?: string;
  fill: 'yellowFlarion' | 'white';
  className?: string;
  onClick?: () => void;
  size?: 'small' | 'large';
  iconSrc?: string;
}

/**
 * Formats a number to display fully if less than 100,000
 * or with K, M, bn suffixes for larger numbers, showing up to 4 significant digits
 */
const formatNumber = (value: number): string => {
  if (value < 100000) {
    // Display numbers less than 100,000 fully
    return value.toLocaleString();
  } else if (value < 1000000) {
    // For numbers between 100,000 and 1,000,000, use K
    const divider = 1000;
    const wholePart = Math.floor(value / divider);
    
    // If the whole part has 3 digits (100K+), we can show 1 decimal place
    if (wholePart >= 100) {
      const formatted = (value / divider).toFixed(1);
      return formatted.endsWith('.0') ? `${formatted.slice(0, -2)}K` : `${formatted}K`;
    }
    
    // If the whole part has 2 digits (10K-99K), we can show 2 decimal places
    if (wholePart >= 10) {
      const formatted = (value / divider).toFixed(2);
      return formatted.endsWith('0')
        ? formatted.endsWith('.00')
          ? `${formatted.slice(0, -3)}K`
          : `${formatted.slice(0, -1)}K`
        : `${formatted}K`;
    }
    
    // If the whole part has 1 digit (1K-9K), we can show 3 decimal places
    const formatted = (value / divider).toFixed(3);
    return formatted.replace(/\.?0+$/, '') + 'K';
  } else if (value < 1000000000) {
    // For numbers between 1,000,000 and 1,000,000,000, use M
    const divider = 1000000;
    const wholePart = Math.floor(value / divider);
    
    // If the whole part has 3 digits (100M+), we can show 1 decimal place
    if (wholePart >= 100) {
      const formatted = (value / divider).toFixed(1);
      return formatted.endsWith('.0') ? `${formatted.slice(0, -2)}M` : `${formatted}M`;
    }
    
    // If the whole part has 2 digits (10M-99M), we can show 2 decimal places
    if (wholePart >= 10) {
      const formatted = (value / divider).toFixed(2);
      return formatted.endsWith('0')
        ? formatted.endsWith('.00')
          ? `${formatted.slice(0, -3)}M`
          : `${formatted.slice(0, -1)}M`
        : `${formatted}M`;
    }
    
    // If the whole part has 1 digit (1M-9M), we can show 3 decimal places
    const formatted = (value / divider).toFixed(3);
    // Remove trailing zeros but keep significant digits
    return formatted.replace(/\.?0+$/, '') + 'M';
  } else {
    // For numbers 1,000,000,000 and above, use bn
    const divider = 1000000000;
    const wholePart = Math.floor(value / divider);
    
    // If the whole part has 3 digits (100bn+), we can show 1 decimal place
    if (wholePart >= 100) {
      const formatted = (value / divider).toFixed(1);
      return formatted.endsWith('.0') ? `${formatted.slice(0, -2)}bn` : `${formatted}bn`;
    }
    
    // If the whole part has 2 digits (10bn-99bn), we can show 2 decimal places
    if (wholePart >= 10) {
      const formatted = (value / divider).toFixed(2);
      return formatted.endsWith('0')
        ? formatted.endsWith('.00')
          ? `${formatted.slice(0, -3)}bn`
          : `${formatted.slice(0, -1)}bn`
        : `${formatted}bn`;
    }
    
    // If the whole part has 1 digit (1bn-9bn), we can show 3 decimal places
    const formatted = (value / divider).toFixed(3);
    // Remove trailing zeros but keep significant digits
    return formatted.replace(/\.?0+$/, '') + 'bn';
  }
};

const MetricCardDesigned: React.FC<MetricCardDesignedProps> = ({
  title,
  subtitle,
  titleValue,
  titlePrefix,
  titleSuffix,
  subtitleValue,
  smallBoxValue,
  smallBoxPrefix,
  smallBoxSuffix,
  fill = 'yellowFlarion',
  className = '',
  onClick,
  size = 'large',
  iconSrc,
}) => {
  const { theme } = useTheme();
  // const { design } = useDesign();
  const isDark = theme === 'dark';

  // Define color schemes with dark mode support
  const colorSchemes = {
    yellowFlarion: {
      background: isDark ? `bg-[${designConfig.colors.flarionYellow}]` : 'bg-[#FDFF37]',
      text: isDark ? 'text-black' : 'text-gray-800',
      textDarker: isDark ? 'text-black' : 'text-gray-900',
      textSubtle: isDark ? 'text-gray-900' : 'text-gray-700',
      smallBoxBg: isDark ? 'bg-transparent' : 'bg-gray-800',
      smallBoxText: isDark ? 'text-black' : 'text-[#f0f0f0]',
      smallBoxBorder: isDark ? 'border border-black' : '',
      buttonBg: isDark ? 'bg-[#2a2a2a]' : 'bg-white',
      buttonIcon: isDark ? 'text-gray-200' : 'text-gray-800',
    },
    white: {
      background: isDark ? 'bg-[#1a1a1a]' : 'bg-white',
      text: isDark ? 'text-[#f0f0f0]' : 'text-gray-800',
      textDarker: isDark ? 'text-[#f0f0f0]' : 'text-gray-900',
      textSubtle: isDark ? 'text-gray-300' : 'text-gray-700',
      smallBoxBg: isDark ? 'bg-transparent' : 'bg-gray-800',
      smallBoxText: isDark ? 'text-gray-400' : 'text-[#f0f0f0]',
      smallBoxBorder: isDark ? 'border border-gray-400' : '',
      buttonBg: isDark ? 'bg-[#2a2a2a]' : 'bg-white',
      buttonIcon: isDark ? 'text-gray-200' : 'text-gray-800',
    },
  };

  const colors = colorSchemes[fill];

  // Format the numbers
  const formattedTitleValue = formatNumber(titleValue);
  const formattedSubtitleValue = subtitleValue !== undefined ? formatNumber(subtitleValue) : undefined;
  const formattedSmallBoxValue = smallBoxValue !== undefined ? formatNumber(smallBoxValue) : undefined;

  // Title value size based on the size prop
  const titleValueSizeClass = size === 'small' ? 'text-4xl' : 'text-6xl';

  return (
    <div
      className={`${colors.background} rounded-3xl p-6 w-full h-full relative ${className}`}
      style={{ minHeight: size === 'small' ? '180px' : '220px' }}
    >
      {/* Icon button positioned absolutely in the top right corner */}
      <button
        onClick={onClick}
        className="absolute top-4 right-4 bg-transparent w-14 h-14 flex items-center justify-center"
      >
        <div
          className={`
            rounded-full 
            overflow-hidden 
            w-10 
            h-10 
            flex 
            items-center 
            justify-center 
            border-2
            ${
              fill === 'yellowFlarion'
                ? 'border-gray-300'
                : isDark
                ? 'border-white'
                : 'border-gray-300'
            }
          `}
        >
          {iconSrc && (
            <img
              src={iconSrc}
              alt={title}
              className={`${isDark && !(iconSrc.includes('arrow-top-right') && fill === 'yellowFlarion') ? 'invert' : ''}`} 
            />
          )}
        </div>
      </button>

      {/* Main content with improved layout */}
      <div className="flex h-full">
        {/* Left side - for title and main value */}
        <div className="w-2/3 flex flex-col justify-between pr-4">
          {/* Title area */}
          <div className="mb-4">
            <div className={`text-2xl font-normal ${colors.text} leading-tight`}>
              {title}
            </div>
          </div>

          {/* Main value */}
          <div className={`${titleValueSizeClass} font-bold ${colors.textDarker}`}>
            {titlePrefix}
            {formattedTitleValue}
            {titleSuffix}
          </div>
        </div>

        {/* Right side - for subtitle and small box value */}
        <div className="w-1/3 flex flex-col justify-end items-end">
          {/* Subtitle and subtitle value with combined word distribution logic - right aligned */}
          {subtitle && (
            <div className="text-right mb-3">
              {(() => {
                const subtitleWords = subtitle.split(' ');
                const hasSubtitleValue = formattedSubtitleValue !== undefined;
                const totalElements = hasSubtitleValue ? subtitleWords.length + 1 : subtitleWords.length;
                
                // If we have two elements total (including value), stack them vertically
                if (totalElements === 2) {
                  return (
                    <>
                      {hasSubtitleValue && <div className={`${colors.textSubtle}`}>{formattedSubtitleValue}</div>}
                      {subtitleWords.map((word, index) => (
                        <div key={index} className={`${colors.textSubtle}`}>{word}</div>
                      ))}
                    </>
                  );
                }
                // If we have three elements total, keep first two together if possible
                else if (totalElements === 3) {
                  if (hasSubtitleValue) {
                    return (
                      <>
                        <div className={`${colors.textSubtle}`}>{formattedSubtitleValue} {subtitleWords[0]}</div>
                        <div className={`${colors.textSubtle}`}>{subtitleWords.slice(1).join(' ')}</div>
                      </>
                    );
                  } else {
                    return (
                      <>
                        <div className={`${colors.textSubtle}`}>{subtitleWords[0]} {subtitleWords[1]}</div>
                        <div className={`${colors.textSubtle}`}>{subtitleWords[2]}</div>
                      </>
                    );
                  }
                }
                // Otherwise, value on top line, then subtitle text
                else {
                  return (
                    <>
                      {hasSubtitleValue && <div className={`${colors.textSubtle}`}>{formattedSubtitleValue}</div>}
                      <div className={`${colors.textSubtle}`}>{subtitle}</div>
                    </>
                  );
                }
              })()}
            </div>
          )}

          {/* Small box value if present */}
          {smallBoxValue !== undefined && (
            <div
              className={`
                ${colors.smallBoxBg}
                ${colors.smallBoxText}
                ${colors.smallBoxBorder}
                px-4
                py-2
                rounded-full
                font-semibold
                text-center
              `}
            >
              {smallBoxPrefix}
              {formattedSmallBoxValue}
              {smallBoxSuffix}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MetricCardDesigned;